import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const API = window.location.origin;

const DEFAULT_STATS = { baytsRead: 0, wordsSaved: 0, cardsDue: 0, streak: 0, poemsSaved: 0 };

export function useUserStats(userId) {
  const [stats, setStats]       = useState(DEFAULT_STATS);
  const [progress, setProgress] = useState([]);
  const [error, setError]       = useState(false);
  const [loading, setLoading]   = useState(!!userId);
  const [retryKey, setRetryKey] = useState(0);

  const refetch = useCallback(() => setRetryKey(k => k + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();

    async function load() {
      if (!userId) { setLoading(false); return; }
      setError(false);
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setLoading(false); return; }

      const headers = { Authorization: `Bearer ${token}` };

      try {
        let r = await fetch(`${API}/api/users/${userId}/stats`, { signal: ctrl.signal, headers });
        // Retry once on 5xx (cold-start DB failures)
        if (r.status >= 500) {
          await new Promise(res => setTimeout(res, 1000));
          r = await fetch(`${API}/api/users/${userId}/stats`, { signal: ctrl.signal, headers });
        }
        // Auth errors are silent — stats stay at defaults, no banner shown
        if (r.status === 401 || r.status === 403) { setLoading(false); return; }
        if (!r.ok) { setError(true); setLoading(false); return; }
        const data = await r.json();
        if (data && !data.error) setStats(data); else setError(true);
      } catch (err) {
        if (err.name !== 'AbortError') setError(true);
      } finally {
        setLoading(false);
      }

      fetch(`${API}/api/users/${userId}/progress`, { signal: ctrl.signal, headers })
        .then(r => r.json())
        .then(data => { if (data?.progress) setProgress(data.progress); })
        .catch(() => {});
    }

    load();
    return () => ctrl.abort();
  }, [userId, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { stats, progress, error, loading, refetch };
}
